/**
 * Numërimi i protokollit të kontratave — formati që zgjedh kompania.
 *
 * Shenjat: {NR} numri, {VITI} viti (2026), {VITI2} (26), {MUAJI} (10).
 * Parapamja përdor të njëjtat rregulla si serveri (services/contractProtocol.js);
 * numri i radhës vjen nga serveri pas ruajtjes.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { authFetch } from '../../services/apiServices';

const PRESETS = ['{NR}/{VITI}', 'Nr. {NR}/{VITI}', 'KP-{NR}/{VITI}', '{NR}-{MUAJI}/{VITI}'];
const TOKENS = [
  { t: '{NR}', d: 'numri' }, { t: '{VITI}', d: 'viti (2026)' }, { t: '{VITI2}', d: 'viti (26)' }, { t: '{MUAJI}', d: 'muaji (10)' },
];

export function previewProtocol(n, c, date = new Date().toISOString().slice(0, 10)) {
  const format = String(c.format || '').includes('{NR}') ? c.format : '{NR}/{VITI}';
  const digits = Math.min(8, Math.max(1, parseInt(c.digits, 10) || 3));
  return format
    .replace(/\{NR\}/g, String(n).padStart(digits, '0'))
    .replace(/\{VITI\}/g, date.slice(0, 4))
    .replace(/\{VITI2\}/g, date.slice(2, 4))
    .replace(/\{MUAJI\}/g, date.slice(5, 7));
}

export default function ProtocolSettingsDialog({ open, onClose, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    authFetch('/api/contract-protocol-settings').then((r) => r.json())
      .then((j) => setCfg(j?.data || { format: '{NR}/{VITI}', digits: 3, start: 1, reset: 'yearly' }))
      .catch(() => setCfg({ format: '{NR}/{VITI}', digits: 3, start: 1, reset: 'yearly' }));
  }, [open]);

  const set = (k) => (e) => setCfg((c) => ({ ...c, [k]: e.target.value }));
  const validFormat = String(cfg?.format || '').includes('{NR}');

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch('/api/contract-protocol-settings', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cfg),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) throw new Error(j.error || 'Nuk u ruajt.');
      setCfg(j.data);
      onSaved?.(j.data);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Numërimi i protokollit të kontratave</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        {!cfg ? <Stack alignItems="center" py={4}><CircularProgress size={24} /></Stack> : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Çdo kontratë merr numrin e radhës herën e parë që gjenerohet. Në shabllon vendoset me fushën
              «Nr. i protokollit» ({'{!contract_number!}'} në Word).
            </Typography>
            <TextField label="Formati" size="small" value={cfg.format} onChange={set('format')}
              error={!validFormat} helperText={validFormat ? ' ' : 'Formati duhet të ketë {NR}'} />
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: -1.5 }}>
              {PRESETS.map((p) => <Chip key={p} size="small" label={p} variant={cfg.format === p ? 'filled' : 'outlined'}
                color={cfg.format === p ? 'primary' : 'default'} onClick={() => setCfg((c) => ({ ...c, format: p }))} />)}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Shenjat: {TOKENS.map((x) => <Box key={x.t} component="span" sx={{ mr: 1.25 }}><b>{x.t}</b> {x.d}</Box>)}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField label="Shifrat e numrit" size="small" type="number" value={cfg.digits} onChange={set('digits')}
                inputProps={{ min: 1, max: 8 }} sx={{ flex: 1 }} helperText="3 → 001" />
              <TextField label="Numri fillestar" size="small" type="number" value={cfg.start} onChange={set('start')}
                inputProps={{ min: 1 }} sx={{ flex: 1 }} helperText="Vazhdoni numërimin tuaj" />
              <TextField select label="Rinis numërimin" size="small" value={cfg.reset} onChange={set('reset')} sx={{ flex: 1 }} helperText=" ">
                <MenuItem value="yearly">Çdo vit (1 janar)</MenuItem>
                <MenuItem value="never">Kurrë</MenuItem>
              </TextField>
            </Stack>
            <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
              Shembull: <b>{previewProtocol(Math.max(1, parseInt(cfg.start, 10) || 1), cfg)}</b>
              {cfg.next && <> · kontrata e radhës (sipas ruajtjes së fundit): <b>{cfg.next}</b></>}
            </Alert>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Anulo</Button>
        <Button variant="contained" onClick={save} disabled={saving || !cfg || !validFormat}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}>Ruaj</Button>
      </DialogActions>
    </Dialog>
  );
}
